import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, X, Loader2, Mail, Building2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "@/components/Toaster";

/**
 * Owner-initiated multi-room invite dialog.
 *
 * Decisions baked in (per product spec):
 *   • role picker — User / Admin / App Owner.
 *   • multi-room — checklist of every room the Owner can manage.
 *   • Owner-initiated invites skip the public sign-up approval queue;
 *     the invitee is fully active the moment they accept the email.
 *   • For role=User, per-room permissions default to
 *     `DEFAULT_USER_PERMS` on the backend. The Owner can fine-tune
 *     later from each room's Permissions tab.
 *
 * UI principles:
 *   • Compact 2-column layout on wide screens, single column on mobile.
 *   • Submit is disabled until email looks valid AND at least one room
 *     is selected — guard rails the backend enforces visibly here too.
 *   • The dialog returns a friendly summary toast describing exactly
 *     what happened ("Invited X to N rooms · email sent").
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteUserModal({ onClose, onSent }) {
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rooms");
        setRooms(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setLoadingRooms(false);
      }
    })();
  }, []);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const valid = useMemo(
    () => EMAIL_RE.test(email.trim()) && picked.size > 0 && !busy,
    [email, picked, busy]
  );

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/owner/invites", {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        role,
        room_ids: Array.from(picked),
      });
      const verb = data.email_sent ? "sent to" : "queued for";
      const tier = role === "owner" ? "App Owner" : role === "admin" ? "Admin" : "User";
      toast.success(
        `Invite ${verb} ${data.invitee_email} — ${tier} · ${data.rooms.length} room${data.rooms.length === 1 ? "" : "s"}`
      );
      if (!data.email_sent && data.email_error) {
        toast.error(`Email delivery failed: ${data.email_error}`);
      }
      onSent?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="bg-background border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="label-mono text-muted-foreground">— Invite</div>
            <h2 className="mt-1 font-display text-2xl tracking-tight">Invite a new user</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              They'll receive an email with a sign-in link. No approval queue —
              you're approving them by sending the invite.
            </p>
          </div>
          <button
            data-testid="invite-modal-close"
            onClick={() => !busy && onClose?.()}
            className="p-2 rounded-md hover:bg-secondary transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {/* Identity */}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="label-mono text-muted-foreground mb-1.5">Email</div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  data-testid="invite-email-input"
                  autoFocus
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full pl-10 pr-3 py-2 bg-secondary rounded-md border border-border focus:border-primary outline-none text-sm"
                />
              </div>
            </label>
            <label className="block">
              <div className="label-mono text-muted-foreground mb-1.5">Display name (optional)</div>
              <input
                data-testid="invite-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-3 py-2 bg-secondary rounded-md border border-border focus:border-primary outline-none text-sm"
              />
            </label>
          </div>

          {/* Role picker */}
          <div>
            <div className="label-mono text-muted-foreground mb-2">Role</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "user",  label: "User",        hint: "View + Encrypted PDF" },
                { key: "admin", label: "Admin",       hint: "Full room access" },
                { key: "owner", label: "App Owner",   hint: "Platform-wide control" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`invite-role-${opt.key}`}
                  onClick={() => setRole(opt.key)}
                  className={`text-left p-3 rounded-md border transition ${
                    role === opt.key
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Data room picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-mono text-muted-foreground">Data rooms</div>
              {!loadingRooms && rooms.length > 0 && (
                <button
                  data-testid="invite-rooms-all"
                  onClick={() =>
                    setPicked(
                      picked.size === rooms.length
                        ? new Set()
                        : new Set(rooms.map((r) => r.id))
                    )
                  }
                  className="text-xs text-primary hover:underline"
                >
                  {picked.size === rooms.length ? "Clear" : "Select all"}
                </button>
              )}
            </div>
            <div
              data-testid="invite-room-list"
              className="border border-border rounded-md max-h-56 overflow-y-auto"
            >
              {loadingRooms ? (
                <div className="p-6 flex items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading rooms…
                </div>
              ) : rooms.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  No data rooms found.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {rooms.map((r) => {
                    const on = picked.has(r.id);
                    return (
                      <li key={r.id}>
                        <label
                          data-testid={`invite-room-${r.id}`}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary transition ${
                            on ? "bg-primary/5" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle(r.id)}
                            className="accent-primary"
                          />
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm flex-1 truncate">{r.name || "Untitled room"}</span>
                          {on && (
                            <span className="label-mono text-primary">Selected</span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {picked.size > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {picked.size} room{picked.size === 1 ? "" : "s"} selected ·{" "}
                {role === "user"
                  ? "Default permissions (View + Encrypted PDF) — adjust in each room's Permissions tab afterwards."
                  : "Full room access (admin role bypasses per-document permissions)."}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-background">
          <button
            data-testid="invite-cancel-btn"
            onClick={() => !busy && onClose?.()}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="invite-send-btn"
            onClick={submit}
            disabled={!valid}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {busy ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}
